import React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import cn from '../utils/cn';
import { controlHeights } from './KoobInputText';
import KoobSpinner from './KoobSpinner';

export const buttonVariants = cva(
  'inline-flex items-center justify-center whitespace-nowrap font-medium cursor-pointer transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 disabled:cursor-not-allowed [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      rounded: {
        sm: 'rounded-sm',
        md: 'rounded-md',
        full: 'rounded-full',
      },
      variant: {
        primary: 'border-none bg-brand text-brand-foreground hover:bg-brand/90',
        black: 'border-none bg-gray-900 text-white hover:bg-gray-800',
        navy: 'border-none bg-k-h text-white hover:bg-k-h/90',
        secondary:
          'border border-solid border-input bg-background hover:bg-accent hover:text-accent-foreground',
        danger: 'border-none bg-red-500 text-white hover:bg-red-500/90',
        /** Destructive but secondary: red outline on a white button. */
        dangerOutline:
          'border border-solid border-red-200 bg-background text-red-600 hover:border-red-300 hover:bg-red-50',
        success: 'border-none bg-green-500 text-white hover:bg-green-500/90',
        ghost: 'border-none bg-transparent text-brand hover:bg-accent',
        // Quiet secondary actions: icon rows under a message, toolbars.
        subtle:
          'border-none bg-transparent text-muted-foreground hover:bg-accent hover:text-foreground',
        soft: 'border-none bg-brand/10 text-brand hover:bg-brand/20',
        outline:
          'border-2 border-solid border-brand bg-background text-brand hover:bg-brand hover:text-brand-foreground',
        /** Light brand tint on hover, focus and while its menu is open. */
        brandOutline:
          'border border-solid border-brand bg-background text-brand hover:bg-brand/10 focus-visible:bg-brand/10 data-[state=open]:bg-brand/10',
      },
      // Heights come from controlHeights so buttons line up with inputs in
      // form rows; lg is md height with wider padding for standalone CTAs.
      size: {
        sm: `${controlHeights.sm} px-3 text-xs gap-1.5`,
        md: `${controlHeights.md} px-4 text-sm gap-2`,
        // Square, for an icon alone (give it an aria-label).
        icon: 'size-7 p-0 text-xs',
        lg: `${controlHeights.md} px-8 text-sm gap-2`,
      },
    },
    defaultVariants: {
      variant: 'secondary',
      size: 'md',
      rounded: 'md',
    },
  },
);

type KoobButtonProps = Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  'className'
> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
    block?: boolean;
    loading?: boolean;
    /** @deprecated use variant */
    color?: VariantProps<typeof buttonVariants>['variant'];
    /** @deprecated use data-testid */
    testId?: string;
  };

const KoobButton = React.forwardRef<HTMLButtonElement, KoobButtonProps>(
  (buttonProps, ref) => {
    const {
      variant,
      color,
      size,
      rounded,
      block = false,
      loading = false,
      asChild = false,
      disabled,
      testId,
      // className is not part of the API (see packages/ui/README.md) but is
      // still stripped here so a stray one can't clobber the computed classes
      className,
      children,
      // ponytail: default 'button' (not shadcn's implicit 'submit') — legacy
      // call sites sit inside forms and always pass type="submit" explicitly
      type = 'button',
      ...props
    } = buttonProps as typeof buttonProps & { className?: string };
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        ref={ref}
        type={asChild ? undefined : type}
        className={cn(
          buttonVariants({ variant: variant ?? color, size, rounded }),
          block && 'w-full',
          loading && 'relative',
          // :disabled doesn't match <a>; block Slot-rendered links by hand
          // ponytail: pointer-events only — keyboard activation not blocked
          asChild && (disabled || loading) && 'pointer-events-none opacity-50',
        )}
        aria-disabled={asChild && (disabled || loading) ? true : undefined}
        aria-busy={loading || undefined}
        disabled={asChild ? undefined : disabled || loading}
        data-testid={testId}
        {...props}
      >
        {/* asChild (Slot) requires a single child: pass children through
            untouched and never emit sibling nodes (even falsy ones — a
            `{false}` sibling makes Slot count >1 child and throw). */}
        {asChild ? (
          children
        ) : (
          <>
            {loading && (
              <span className="absolute inset-0 flex items-center justify-center">
                <KoobSpinner color="currentColor" size={16} />
              </span>
            )}
            {/* keep children in flow so width is preserved; hide while loading */}
            {loading ? (
              <span className="invisible contents">{children}</span>
            ) : (
              children
            )}
          </>
        )}
      </Comp>
    );
  },
);

KoobButton.displayName = 'KoobButton';

export default KoobButton;
